import { ContainerType, ExecutorJobsType, ExecutorType } from "common";

export const ProxyOptions = [
  {
    label: "None",
    value: "none",
  },
  {
    label: "HTTP",
    value: "http",
  },
];

export const AppStatusOptions = [
  {
    label: "Active",
    value: "active",
  },
  {
    label: "Inactive",
    value: "inactive",
  },
];

export const WebsocketReconnectionOptions = [
  {
    label: "None (default)",
    value: "none",
  },
  {
    label: "Confirm",
    value: "confirm",
  },
  {
    label: "Auto",
    value: "auto",
  },
];

export const ContainerTypeOptions = [
  {
    label: "Docker",
    value: ContainerType.Docker,
  },
  {
    label: "Singularity",
    value: ContainerType.Singularity,
  },
];

/**
 * The executors of Nextflow a run environment can be saved with.
 *
 * Local stays first because the forms read `ExecutorOptions[0]` as Local.
 */
export const ExecutorOptions = [
  {
    label: "Local",
    value: ExecutorType.Local,
  },
  {
    label: "LSF",
    value: ExecutorType.LSF,
  },
  {
    label: "SLURM",
    value: ExecutorType.SLURM,
  },
  {
    label: "SGE",
    value: ExecutorType.SGE,
  },
];

/**
 * The option an executor select shows for a saved value.
 */
export const executorSelectValue = (
  executor: ExecutorType | null | undefined
): (typeof ExecutorOptions)[number] | undefined =>
  ExecutorOptions.find(({ value }) => value === executor);

export const ExecutorJobOptions = [
  {
    label: "Local",
    value: ExecutorJobsType.Local,
  },
  {
    label: "SGE",
    value: ExecutorJobsType.Sge,
  },
  {
    label: "LSF",
    value: ExecutorJobsType.Lsf,
  },
  {
    label: "SLURM",
    value: ExecutorJobsType.Slurm,
  },
  {
    label: "IGNITE",
    value: ExecutorJobsType.Ignite,
  },
  {
    label: "AWS Batch",
    value: ExecutorJobsType.AwsBatch,
  },
  {
    label: "Google Batch",
    value: ExecutorJobsType.GoogleBatch,
  },
];

export const ExecutorJobsLocals = [
  ExecutorJobsType.Local,
  ExecutorJobsType.GoogleBatch,
  ExecutorJobsType.Ignite,
];

export const ExecutorJobsExternals = [
  ExecutorJobsType.Sge,
  ExecutorJobsType.Lsf,
  ExecutorJobsType.Slurm,
  ExecutorJobsType.AwsBatch,
];

export const ExecutorLocalFormItems = [
  {
    title: "Memory (GB)",
    registerValue: "nextMemory",
    placeholderText: "Your Memory",
  },
  {
    title: "CPUs",
    registerValue: "nextCpu",
    placeholderText: "Your CPUs",
  },
];

export const ExecutorExternalFormItems = [
  {
    title: "Queue",
    registerValue: "nextQueue",
    placeholderText: "Your Queue",
  },
  {
    title: "Memory (GB)",
    registerValue: "nextMemory",
    placeholderText: "Your Memory",
  },
  {
    title: "CPUs",
    registerValue: "nextCpu",
    placeholderText: "Your CPUs",
  },
  {
    title: "Time(min.)",
    registerValue: "nextTime",
    placeholderText: "Your Time(min.)",
  },
  {
    title: "Other Options",
    registerValue: "nextCluOpt",
    placeholderText: "Your Other Options",
  },
];

export const ExecutorJobsLocalFormItems = [
  {
    title: "Memory (GB)",
    registerValue: "jobMemory",
    placeholderText: "Your Memory",
  },
  {
    title: "CPUs",
    registerValue: "jobCpu",
    placeholderText: "Your CPUs",
  },
];

export const ExecutorJobsLocalInitialFormItems = [
  {
    title: "Memory (GB)",
    registerValue: "initJobMemory",
    placeholderText: "Your Memory",
  },
  {
    title: "CPUs",
    registerValue: "initJobCpu",
    placeholderText: "Your CPUs",
  },
];

export const ExecutorJobsExternalFormItems = [
  {
    title: "Queue",
    registerValue: "jobQueue",
    placeholderText: "Your Queue",
  },
  {
    title: "Memory (GB)",
    registerValue: "jobMemory",
    placeholderText: "Your Memory",
  },
  {
    title: "CPUs",
    registerValue: "jobCpu",
    placeholderText: "Your CPUs",
  },
  {
    title: "Time(min.)",
    registerValue: "jobTime",
    placeholderText: "Your Time(min.)",
  },
  {
    title: "Other Options",
    registerValue: "jobCluOpt",
    placeholderText: "Your Other Options",
  },
];

export const ExecutorJobsExternalInitialFormItems = [
  {
    title: "Queue",
    registerValue: "initJobQueue",
    placeholderText: "Your Queue",
  },
  {
    title: "Memory (GB)",
    registerValue: "initJobMemory",
    placeholderText: "Your Memory",
  },
  {
    title: "CPUs",
    registerValue: "initJobCpu",
    placeholderText: "Your CPUs",
  },
  {
    title: "Time(min.)",
    registerValue: "initJobTime",
    placeholderText: "Your Time(min.)",
  },
  {
    title: "Other Options",
    registerValue: "initJobCluOpt",
    placeholderText: "Your Other Options",
  },
];

export const ExecutorJobsMaximumLimitFormItems = [
  {
    title: "Memory (GB)",
    registerValue: "execJobMaxMemory",
    placeholderText: "Your Memory",
  },
  {
    title: "CPUs",
    registerValue: "execJobMaxCpu",
    placeholderText: "Your CPUs",
  },
];

export const DefaultValues = {
  // SSH is what every environment used before the agent transport existed,
  // and the field is required on the payload, so a new form has to send
  // something even when the user never opens the Basics card's radio group.
  // "as const" keeps this a RunEnvironmentTransport literal rather than the
  // wider string type useForm's defaultValues then refuses to accept.
  transport: "ssh" as const,
  nextQueue: "Default",
  nextTime: "100",
  nextMemory: "32",
  nextCpu: "1",
  jobQueue: "Default",
  jobMemory: "16",
  jobCpu: "1",
  jobTime: "100",
  initJobQueue: "Default",
  initJobMemory: "16",
  initJobCpu: "1",
  initJobTime: "100",
  execJobMaxMemory: 380,
  execJobMaxCpu: 30,
  containerOptions: "",
};
